/**
 * compliance.ts — live compliance alert copy (GUI v2 req G.3).
 *
 * Source of truth: 24_live-ai-coaching-prompts.md §D (alert table, verbatim)
 * + 26_claims-compliance-guardrails.md (approved alternatives). These render
 * red, interrupt the nudge queue immediately, and always include the approved
 * alternative phrasing. Prohibited-claim and improvised-legal alerts are also
 * automatic-fail conditions (18_call-scorecard.md §5).
 */
import type { ComplianceAlertId } from "@/types";

export interface ComplianceAlertDef {
  id: ComplianceAlertId;
  /** STOP = prohibited claim; FIX = correct the framing. (24 §D styles.) */
  kind: "STOP" | "FIX";
  /** On-screen alert, verbatim from 24 §D. */
  alert: string;
  /** The approved alternative the advisor can say instead (26). */
  approved: string;
  /** Counts as an automatic fail on the scorecard (18 §5). */
  autoFail: boolean;
  source: string;
}

export const COMPLIANCE_ALERTS: ComplianceAlertDef[] = [
  {
    id: "fuel_numbers",
    kind: "STOP",
    alert: "No MPG figures, fuel dollar amounts, or payback promises.",
    approved:
      "Less heat in the cabin means the A/C works less, and an A/C that runs less uses less fuel. Comfort first — no numbers.",
    autoFail: true,
    source: "24_live-ai-coaching-prompts.md §D · 26 §1",
  },
  {
    id: "legal_improvised",
    kind: "STOP",
    alert: "Official guidance only. Never improvise legal advice.",
    approved:
      "Here is our official guidance for Indiana: front sides 30% VLT; rear sides 30% cars, 5% SUVs/vans/trucks.",
    autoFail: true,
    source: "24_live-ai-coaching-prompts.md §D · 26 §4",
  },
  {
    id: "coating_as_chip_protection",
    kind: "STOP",
    alert: "Coating is not rock chip protection. That is PPF.",
    approved: "PPF protects against physical impact; coating is gloss, easier cleaning, and maintenance.",
    autoFail: true,
    source: "24_live-ai-coaching-prompts.md §D · 26 §8",
  },
  {
    id: "financing_guarantee",
    kind: "STOP",
    alert: "Say “for qualified applicants.” Never guarantee approval.",
    approved: "Synchrony Bank financing, 6 to 12 months no interest for qualified applicants.",
    autoFail: true,
    source: "24_live-ai-coaching-prompts.md §D · 26 §10",
  },
  {
    id: "flat_heat_number",
    kind: "FIX",
    alert: "Tie specs to line and shade.",
    approved: "“Up to X percent depending on shade” — IRR may be stated per line.",
    autoFail: true,
    source: "24_live-ai-coaching-prompts.md §D · 26 §2",
  },
  {
    id: "off_sheet_spec",
    kind: "STOP",
    alert: "Spec sheet numbers only.",
    approved: "Black ≤43% TSER / 13% IRR · Black Ceramic ≤63% / 60% · i3 ≤71% / 93% · i3+ 62–74% / 99%.",
    autoFail: true,
    source: "24_live-ai-coaching-prompts.md §D · 26 §2",
  },
  {
    id: "scratch_proof",
    kind: "STOP",
    alert: "Never “scratch-proof” or “damage-proof.” State honest limits.",
    approved: "A coating resists light wash marring — it does not stop scratches from impacts, keys, or brushes.",
    autoFail: true,
    source: "24_live-ai-coaching-prompts.md §D · 26 §7",
  },
  {
    id: "ppf_install_by_phone",
    kind: "STOP",
    alert: "PPF requires a showroom consult first.",
    approved: "Book the consult, not the install — the consult produces the 100% accurate quote.",
    autoFail: false,
    source: "24_live-ai-coaching-prompts.md §D · 02 §7",
  },
  {
    id: "clock_time_promise",
    kind: "FIX",
    alert: "Use drop-off framing, not clock-time promises.",
    approved: "In by 11:00 am for same-day completion — we call the moment it is finished.",
    autoFail: false,
    source: "24_live-ai-coaching-prompts.md §D · 02 §6",
  },
];

export function complianceAlert(id: ComplianceAlertId): ComplianceAlertDef {
  const found = COMPLIANCE_ALERTS.find((a) => a.id === id);
  if (!found) throw new Error(`unknown compliance alert ${id}`);
  return found;
}
